// src/layout/tubeLayoutEngine.ts
// Absorbed from layout/tubeLayoutEngine.js (Task 36)
'use strict';

import type { Item } from '../types';

/** A mutable layout node wrapping an item for a single solver pass. */
interface TubeNode {
  item: Item;
  x: number;
  y: number;
  scale: number;
  desiredScale: number;
  pinned: boolean;
}

/** Resolved tube-edge bounds for an item ring. */
interface TubeEdges {
  innerEdge: number;
  outerEdge: number;
}

/** Obstacle to push items away from (e.g. scanner, conveyor temp item). */
export interface LayoutObstacle {
  x: number;
  y: number;
  radius: number;
  gap: number;
}

/** Merged numeric config used during solver passes. */
interface ResolvedConfig {
  pairGap: number;
  innerPad: number;
  outerPad: number;
  centerGuardRadius: number;
  scenePadding: number;
  anchorPull: number;
  pairPasses: number;
  finalPasses: number;
  scaleLerp: number;
  flowLerpBase: number;
  flowLerpBoost: number;
  pressureShrink: number;
  pressureMaxShrink: number;
  scaleMin: number;
  scaleMax: number;
  primaryPinWeight: number;
}

/** Caller-supplied config overrides (may include nested shorthand objects). */
export interface LayoutConfigInput {
  pairGap?: number;
  innerPad?: number;
  outerPad?: number;
  centerGuardRadius?: number;
  scenePadding?: number;
  anchorPull?: number;
  pairPasses?: number;
  finalPasses?: number;
  scaleLerp?: number;
  flowLerpBase?: number;
  flowLerpBoost?: number;
  pressureShrink?: number;
  pressureMaxShrink?: number;
  scaleMin?: number;
  scaleMax?: number;
  primaryPinWeight?: number;
  tubePads?: { inner?: number; outer?: number; scene?: number };
  scaleLimits?: { min?: number; max?: number };
  solverPasses?: { pair?: number; final?: number };
  flowLerp?: { base?: number; boost?: number };
}

/** Per-frame context passed by the caller. */
export interface LayoutFrameCtx {
  focusX?: number;
  focusY?: number;
  posLerp?: number;
  obstacles?: LayoutObstacle[];
  pinnedItems?: Set<Item>;
  config?: LayoutConfigInput;
  centerGuardRadius?: number;
}

/** Per-call state bag passed by the caller. */
export interface LayoutState {
  items: Item[];
  cx: number;
  cy: number;
  highlighted: Item | null;
  applyItemTransform: (item: Item) => void;
  getRenderScale?: (item: Item) => number;
  config?: LayoutConfigInput;
  centerGuardRadius?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function safeHypot(x: number, y: number): number {
  return Math.hypot(x, y) || 0.0001;
}

const LAYOUT_CONFIG = {
  pairGap: 12,
  innerPad: 8,
  outerPad: 12,
  centerGuardRadius: 100,
  scenePadding: 4,
  anchorPull: 0.2,
  pairPasses: 6,
  finalPasses: 2,
  scaleLerp: 0.18,
  flowLerpBase: 0.12,
  flowLerpBoost: 0.18,
  pressureShrink: 0.12,
  pressureMaxShrink: 0.2,
  scaleMin: 0.52,
  scaleMax: 2.1,
  primaryPinWeight: 6,
};

function applyFiniteConfig(target: ResolvedConfig, key: keyof ResolvedConfig, value: number | undefined): void {
  if (Number.isFinite(value)) {
    (target as Record<keyof ResolvedConfig, number>)[key] = value as number;
  }
}

function mergeLayoutConfig(stateConfig: LayoutConfigInput | null, frameConfig: LayoutConfigInput | null): ResolvedConfig {
  const merged: ResolvedConfig = Object.assign({}, LAYOUT_CONFIG, stateConfig || {}, frameConfig || {});
  const sources = [stateConfig, frameConfig];

  for (let i = 0; i < sources.length; i += 1) {
    const cfg = sources[i];
    if (!cfg) continue;

    if (cfg.tubePads) {
      applyFiniteConfig(merged, 'innerPad', cfg.tubePads.inner);
      applyFiniteConfig(merged, 'outerPad', cfg.tubePads.outer);
      applyFiniteConfig(merged, 'scenePadding', cfg.tubePads.scene);
    }

    if (cfg.scaleLimits) {
      applyFiniteConfig(merged, 'scaleMin', cfg.scaleLimits.min);
      applyFiniteConfig(merged, 'scaleMax', cfg.scaleLimits.max);
    }

    if (cfg.solverPasses) {
      applyFiniteConfig(merged, 'pairPasses', cfg.solverPasses.pair);
      applyFiniteConfig(merged, 'finalPasses', cfg.solverPasses.final);
    }

    if (cfg.flowLerp) {
      applyFiniteConfig(merged, 'flowLerpBase', cfg.flowLerp.base);
      applyFiniteConfig(merged, 'flowLerpBoost', cfg.flowLerp.boost);
    }
  }

  return merged;
}

function getItemRadius(item: Item, scale: number): number {
  const resolvedScale = Math.max(scale || 1, 0.01);
  return Math.max(1, item.size * 0.5 * resolvedScale);
}

function resolveTubeEdges(items: Item[], cx: number, cy: number, config: ResolvedConfig): TubeEdges | null {
  if (!items || !items.length) return null;

  let innerEdge = Infinity;
  let outerEdge = 0;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]!;
    const baseRadius = Math.hypot(item.baseX - cx, item.baseY - cy);
    const itemRadius = Math.max(1, item.size * 0.5);
    innerEdge = Math.min(innerEdge, baseRadius - itemRadius);
    outerEdge = Math.max(outerEdge, baseRadius + itemRadius);
  }

  if (!Number.isFinite(innerEdge) || !Number.isFinite(outerEdge)) return null;

  const sceneOuterEdge = Math.min(cx, cy) - config.scenePadding;
  const resolvedInner = Math.max(0, innerEdge - config.innerPad);
  const resolvedOuter = Math.min(sceneOuterEdge, outerEdge + config.outerPad);

  if (resolvedOuter <= resolvedInner + 2) return null;
  return {
    innerEdge: resolvedInner,
    outerEdge: resolvedOuter,
  };
}

function clampNodeToTube(node: TubeNode, tubeEdges: TubeEdges | null, cx: number, cy: number, config: ResolvedConfig, centerGuardRadius: number): void {
  const radius = getItemRadius(node.item, node.scale);
  const minCenterDist = Math.max(radius, centerGuardRadius + radius);
  const sceneOuterEdge = Math.min(cx, cy) - config.scenePadding;
  let maxCenterDist = Math.max(minCenterDist, sceneOuterEdge - radius);

  if (tubeEdges) {
    const tubeMin = Math.max(minCenterDist, tubeEdges.innerEdge + radius);
    const tubeMax = Math.min(maxCenterDist, tubeEdges.outerEdge - radius);
    if (tubeMax >= tubeMin) {
      maxCenterDist = tubeMax;
    }
  }

  if (maxCenterDist < minCenterDist) {
    maxCenterDist = minCenterDist;
  }

  const ox = node.x - cx;
  const oy = node.y - cy;
  const dist = safeHypot(ox, oy);

  if (dist > maxCenterDist) {
    const outScale = maxCenterDist / dist;
    node.x = cx + ox * outScale;
    node.y = cy + oy * outScale;
    return;
  }

  if (dist < minCenterDist) {
    const inScale = minCenterDist / dist;
    node.x = cx + ox * inScale;
    node.y = cy + oy * inScale;
  }
}

function applyPairSpacing(nodes: TubeNode[], primaryItem: Item | null, config: ResolvedConfig, pressure: Map<Item, number>): void {
  for (let i = 0; i < nodes.length; i += 1) {
    const a = nodes[i]!;
    for (let j = i + 1; j < nodes.length; j += 1) {
      const b = nodes[j]!;
      const ar = getItemRadius(a.item, a.scale);
      const br = getItemRadius(b.item, b.scale);
      const minDist = ar + br + config.pairGap;

      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = safeHypot(dx, dy);
      if (dist >= minDist) continue;

      const overlap = minDist - dist;
      const nx = dx / dist;
      const ny = dy / dist;

      const aWeight = a.item === primaryItem ? config.primaryPinWeight : (a.pinned ? 4 : 1);
      const bWeight = b.item === primaryItem ? config.primaryPinWeight : (b.pinned ? 4 : 1);
      const total = aWeight + bWeight;
      const moveA = bWeight / total;
      const moveB = aWeight / total;

      if (!a.pinned) {
        a.x -= nx * overlap * moveA;
        a.y -= ny * overlap * moveA;
        pressure.set(a.item, (pressure.get(a.item) || 0) + overlap / Math.max(1, minDist));
      }

      if (!b.pinned) {
        b.x += nx * overlap * moveB;
        b.y += ny * overlap * moveB;
        pressure.set(b.item, (pressure.get(b.item) || 0) + overlap / Math.max(1, minDist));
      }
    }
  }
}

function applyObstacleSpacing(nodes: TubeNode[], obstacles: LayoutObstacle[], config: ResolvedConfig, pressure: Map<Item, number>): void {
  if (!obstacles || !obstacles.length) return;

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]!;
    if (node.pinned) continue;

    for (let j = 0; j < obstacles.length; j += 1) {
      const obstacle = obstacles[j]!;
      if (!obstacle || !Number.isFinite(obstacle.radius)) continue;

      const gap = Number.isFinite(obstacle.gap) ? Math.max(0, obstacle.gap) : config.pairGap;
      const nr = getItemRadius(node.item, node.scale);
      const minDist = nr + obstacle.radius + gap;

      const dx = node.x - obstacle.x;
      const dy = node.y - obstacle.y;
      const dist = safeHypot(dx, dy);
      if (dist >= minDist) continue;

      const overlap = minDist - dist;
      node.x += (dx / dist) * overlap;
      node.y += (dy / dist) * overlap;
      pressure.set(node.item, (pressure.get(node.item) || 0) + overlap / Math.max(1, minDist));
    }
  }
}

function applyScalePressure(nodes: TubeNode[], primaryItem: Item | null, config: ResolvedConfig, pressure: Map<Item, number>): void {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]!;
    if (node.pinned || node.item === primaryItem) continue;

    const p = pressure.get(node.item) || 0;
    if (p <= 0) continue;

    const shrink = clamp(p * config.pressureShrink, 0, config.pressureMaxShrink);
    node.scale = clamp(node.scale * (1 - shrink), config.scaleMin, config.scaleMax);
  }
}

function stepTubeLayout(state: LayoutState, frameCtx: LayoutFrameCtx): void {
  if (!state || !Array.isArray(state.items) || state.items.length === 0) return;

  const items = state.items;
  const cx = state.cx;
  const cy = state.cy;
  const highlighted = state.highlighted || null;
  const applyItemTransform = state.applyItemTransform;
  const getRenderScale = state.getRenderScale || function () { return 1; };

  const config = mergeLayoutConfig(state.config || null, frameCtx && frameCtx.config ? frameCtx.config : null);
  const centerGuardRadius: number = Number.isFinite(state.centerGuardRadius)
    ? (state.centerGuardRadius as number)
    : (frameCtx && Number.isFinite(frameCtx.centerGuardRadius) ? (frameCtx.centerGuardRadius as number) : config.centerGuardRadius);

  const tubeEdges = resolveTubeEdges(items, cx, cy, config);
  const pinnedItems = (frameCtx && frameCtx.pinnedItems) || new Set();
  const obstacles = (frameCtx && frameCtx.obstacles) || [];
  const focusX: number = frameCtx && Number.isFinite(frameCtx.focusX) ? (frameCtx.focusX as number) : cx;
  const focusY: number = frameCtx && Number.isFinite(frameCtx.focusY) ? (frameCtx.focusY as number) : cy;
  const posLerp: number = frameCtx && Number.isFinite(frameCtx.posLerp) ? (frameCtx.posLerp as number) : 0.12;

  const nodes: TubeNode[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]!;
    const desiredScale = Number.isFinite(item.organismDesiredScale)
      ? item.organismDesiredScale
      : (Number.isFinite(item.currentScale) ? item.currentScale : 1);
    nodes.push({
      item: item,
      x: Number.isFinite(item.flowX) ? item.flowX : item.baseX,
      y: Number.isFinite(item.flowY) ? item.flowY : item.baseY,
      scale: clamp(Math.max(item.currentScale || 1, 0.001), config.scaleMin, config.scaleMax),
      desiredScale: clamp(desiredScale, config.scaleMin, config.scaleMax),
      pinned: pinnedItems.has(item),
    });
  }

  for (let pass = 0; pass < config.pairPasses; pass += 1) {
    const pressure = new Map();

    for (let n = 0; n < nodes.length; n += 1) {
      const node = nodes[n]!;
      if (!node.pinned) {
        node.x = lerp(node.x, node.item.baseX, config.anchorPull);
        node.y = lerp(node.y, node.item.baseY, config.anchorPull);
      }
      node.scale = lerp(node.scale, node.desiredScale, 0.28);
      node.scale = clamp(node.scale, config.scaleMin, config.scaleMax);
    }

    applyPairSpacing(nodes, highlighted, config, pressure);
    applyObstacleSpacing(nodes, obstacles, config, pressure);
    applyScalePressure(nodes, highlighted, config, pressure);

    for (let c = 0; c < nodes.length; c += 1) {
      clampNodeToTube(nodes[c]!, tubeEdges, cx, cy, config, centerGuardRadius);
    }
  }

  for (let finalPass = 0; finalPass < config.finalPasses; finalPass += 1) {
    const finalPressure = new Map();
    applyPairSpacing(nodes, highlighted, config, finalPressure);
    applyObstacleSpacing(nodes, obstacles, config, finalPressure);
    applyScalePressure(nodes, highlighted, config, finalPressure);

    for (let ci = 0; ci < nodes.length; ci += 1) {
      clampNodeToTube(nodes[ci]!, tubeEdges, cx, cy, config, centerGuardRadius);
    }
  }

  for (let ni = 0; ni < nodes.length; ni += 1) {
    const outNode = nodes[ni]!;
    const itemOut = outNode.item;
    const distFromFocus = Math.hypot(itemOut.baseX - focusX, itemOut.baseY - focusY);
    const proximity = 1 - clamp(distFromFocus / 420, 0, 1);
    const flowLerp = config.flowLerpBase + proximity * config.flowLerpBoost;

    itemOut.flowX = lerp(itemOut.flowX, outNode.x, flowLerp);
    itemOut.flowY = lerp(itemOut.flowY, outNode.y, flowLerp);
    itemOut.currentX = lerp(itemOut.currentX, itemOut.flowX, posLerp);
    itemOut.currentY = lerp(itemOut.currentY, itemOut.flowY, posLerp);

    const scaleLerp = itemOut.pressed ? Math.max(config.scaleLerp, 0.22) : config.scaleLerp;
    itemOut.currentScale = clamp(lerp(itemOut.currentScale, outNode.scale, scaleLerp), config.scaleMin, config.scaleMax);

    const clampNode: TubeNode = { item: itemOut, x: itemOut.currentX, y: itemOut.currentY, scale: itemOut.currentScale, desiredScale: itemOut.currentScale, pinned: false };
    clampNodeToTube(clampNode, tubeEdges, cx, cy, config, centerGuardRadius);
    itemOut.currentX = clampNode.x;
    itemOut.currentY = clampNode.y;
    itemOut.flowX = lerp(itemOut.flowX, itemOut.currentX, 0.55);
    itemOut.flowY = lerp(itemOut.flowY, itemOut.currentY, 0.55);

    if (typeof applyItemTransform === 'function') {
      applyItemTransform(itemOut);
    }
    if (itemOut.el) {
      itemOut.el.style.opacity = '1';
    }

    // Keep scanner rendering in sync when this item has scanner scale.
    const renderScale = getRenderScale(itemOut);
    if (!Number.isFinite(renderScale) || renderScale <= 0) {
      itemOut.currentScale = Math.max(itemOut.currentScale, 0.001);
    }
  }
}

export const tubeLayoutEngine = {
  LAYOUT_CONFIG,
  stepTubeLayout,
};
